/**
 * How an aeroplane behaves when nobody is flying it (AD-014, AD-014b).
 *
 * Split out of `step.ts` because it is one idea with two halves — what the
 * controls do and what the airframe does — and because the integrator is a
 * pipeline, not a place to keep policy.
 *
 * The provenance matters and is stated once here: **none of this is recovered.**
 * `damage.c` yields the severity ladder and nothing about the aftermath, and
 * `pyro.c` — the module its outright-destruction path feeds — has never been
 * disassembled and carries no strings. The only descriptive anchor in the whole
 * source record is the manual's own history chapter on Richthofen's last flight,
 * which "began to spin down from a dogfight, out of control … then dove for the
 * ground". So these are port decisions tuned to that shape, and every constant
 * they read is CALIBRATION-PENDING.
 */

import { damageRollBias, wreckPitchBias } from "./damage.ts";
import { NEUTRAL_INPUTS, type FlightInputs } from "./inputs.ts";
import type { BodyRates } from "./math3d.ts";
import { controlsLocked, type PilotState } from "./physiology.ts";
import type { DamageState } from "./damage.ts";
import type { Tunables } from "./tunables.ts";
import { clamp, signOf } from "./units.ts";

/**
 * The slumped stick's aileron, bank-limited (AD-014b).
 *
 * AD-014 states the rule for the *pitch* axis and gives the reason: "a constant
 * rate is a loop, not a dive — it tumbles the wreck through inverted and back
 * and averages out to the same mush it was meant to cure." The roll axis wants
 * the same rule, for the same reason and with a sharper consequence: a constant
 * aileron deflection held for the length of a fall is a barrel roll, and each
 * half-revolution puts the body-frame nose-down bias on the *sky* side, where it
 * spends the dive instead of deepening it.
 *
 * A slumped pilot does not fly a barrel roll. What his weight on the stick
 * produces is the **graveyard spiral** the manual's Richthofen passage describes
 * — bank builds, stops building, and the nose falls into a steepening turn. So
 * the aileron is proportional to the bank the wreck has *left to reach*, and
 * reverses if something else rolls it past: a stable attractor at
 * `wreckSpiralBankRad`, the same shape the alpha weathervane already has on the
 * pitch axis. Below that bank the lift vector still has a vertical component and
 * the airframe still descends; it can no longer be carried over the top.
 *
 * The cost of the unbounded version, measured, is in AD-014b §3c: an Albatros
 * D.III killed at 1,200 m reaches −47 m/s at 90° of bank and is climbing at
 * +8.7 m/s nine seconds later, above the altitude it was killed at.
 */
export function wreckStickRoll(bankRad: number, tunables: Tunables): number {
  const target = tunables.wreckSpiralBankRad;
  const side = signOf(bankRad) || 1;
  const remaining = target - Math.abs(bankRad);
  return clamp((remaining / target) * tunables.deadPilotStickRoll, -1, 1) * side;
}

/**
 * The stick, when the man holding it cannot fly.
 *
 * Three cases, and they are not the same. A **blacked-out** pilot is still in
 * his seat and still braced: the stick centres, the guns go quiet, and the
 * aeroplane flies itself until he comes round — which is what makes a greyout
 * survivable.
 *
 * A **dead** pilot does not let go of anything. He slumps forward onto the stick
 * and over to one side, which is what puts the aeroplane into the nose-down,
 * rolled descent the period accounts describe. A centred stick would instead
 * trim the wreck into a tidy hundred-second glide — both wrong, and the reason a
 * kill used to end with the victim still airborne.
 *
 * A **folded airframe** is the third, and it is tested first, because a wreck is
 * a wreck whatever the man in it is doing. `isWreck` — not `controlsLocked` — is
 * what this function asks, so that both halves of this module read one predicate
 * for one idea; `controlsLocked` describes only the pilot, and a wing that has
 * come off is not a fact about the pilot. The ailerons a folded wing carried are
 * folded with it, so a live pilot in a wreck gets the same slumped stick a dead
 * one does: he has nothing left to fly with.
 *
 * Reading only the pilot half leaves an aeroplane with no wings holding every
 * input it was handed, and in an encounter those inputs come from
 * `updateAiPilot`, which knows nothing about `wingsFailed`, `fire` or
 * `AircraftStatus`. It flies the wreck — throttle open, evading, and pulling a
 * `zoom` whenever `classifySituation` returns `lowAltitude`. That is worth a
 * measurement rather than an assurance: a Dr.I with both wing cells shot away
 * holds a *stable powered glide* at 21.6 m/s and −10.1 m/s vertical, wings
 * level, indefinitely, and `wreckFallTimeoutSeconds` writes it off hundreds of
 * metres up rather than any ground arriving. AD-014b §3a.
 *
 * The throttle closes in both wreck cases: nobody holds a wreck's engine open,
 * and the thrust is most of what would hold such a glide up.
 */
export function derelictInputs(
  inputs: FlightInputs,
  pilot: PilotState,
  damage: DamageState,
  bankRad: number,
  tunables: Tunables,
): FlightInputs {
  if (isWreck(damage, pilot)) {
    return {
      ...NEUTRAL_INPUTS,
      pitch: tunables.deadPilotStickPitch,
      roll: wreckStickRoll(bankRad, tunables),
    };
  }
  if (!controlsLocked(pilot)) return inputs;
  return { ...NEUTRAL_INPUTS, throttle: inputs.throttle };
}

/**
 * A wreck is an aeroplane nobody is flying: the wings have folded, the man in
 * the seat is dead, **or he has left it** (AD-021's bail-out).
 *
 * The third case is deliberately the same case as the second and not a new one.
 * A pilot going over the side of an open cockpit shoves the stick forward and
 * rolls the machine over to fall clear — which is what `deadPilotStickPitch`
 * and `wreckStickRoll` already command — so the aeroplane he leaves behind is
 * flown down by AD-014's arc without a second mechanism being written for it.
 */
export function isWreck(damage: DamageState, pilot: PilotState): boolean {
  return damage.wingsFailed || !pilot.alive || pilot.bailedOut;
}

export interface WreckBiasInput {
  readonly damage: DamageState;
  readonly pilot: PilotState;
  readonly tunables: Tunables;
  readonly alphaRad: number;
  /** Whatever the departed-flight model is already commanding (`spin.ts`). */
  readonly stallBias: BodyRates;
}

/**
 * The uncommanded rates a broken aeroplane flies on: the asymmetric-wing roll it
 * always had, plus AD-014's nose-down weathervane. A live pilot with a sound
 * airframe gets neither and sees exactly the rates he did before.
 */
export function wreckRateBias(input: WreckBiasInput): BodyRates {
  const { stallBias, tunables } = input;
  return {
    ...stallBias,
    roll: stallBias.roll + damageRollBias(input.damage, tunables),
    pitch: stallBias.pitch + wreckPitchBias(isWreck(input.damage, input.pilot), tunables, input.alphaRad),
  };
}
